import { Layout, Menu } from 'antd';
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from 'react-redux';
import { makeRequestStateless } from '@utils/helpers';
import { isAdmin } from '@comps/commonFunctions';
import Logo from '@assets/images/logo.png';
import { getNavigationReq, getMyPermissionsReq } from '@mods/general/requests';
import { Icon } from '@iconify/react';
import { SIDER_WIDTH } from '@theme/constants';
import { useCaseCounts } from '../../CaseCounts';
import "./sider.css";

const { Sider } = Layout;

export { SIDER_WIDTH };

// The admin Cases list under Case Management is gone (Create Case and
// delete moved to Total Cases), but its "cases" module/permission node
// stays seeded server-side — cases_create/edit/delete are reused
// throughout userCase — so it is hidden here rather than removed from
// ModuleSeeder, to keep those permissions intact without leaving a dead
// link in the menu. Case Types was dropped from ModuleSeeder outright
// (nothing reuses caseTypes_*), but that only takes effect after the
// module/permission tables are reseeded, which a deploy does not always
// do — hidden here too so it disappears immediately either way.
const HIDDEN_CHILD_KEYS = { caseManagement: new Set(['cases', 'caseTypes']) };

// The work queues that carry a count bubble, keyed by nav route, valued
// by the key the get-case-counts endpoint answers with.
const COUNTED_ROUTES = {
  'cases/new': 'new',
  'cases/modifications': 'modifications',
  // Admins and technicians only (the API leaves it out for a doctor):
  // approved cases still waiting on their STL.
  'cases/approved': 'approved',
  'cases/refinements': 'refinements',
  'cases/on-hold': 'onhold',
};

/**
 * Turn the permission-filtered nav tree into antd Menu items.
 *
 * The backend returns a flat-ish list where a few entries carry children
 * (Case Management, User Management). Those become groups with a label so
 * the ten items stop reading as one undifferentiated column.
 */
const buildMenuItems = (data, permissions, unrestricted = false, parentKey = null, counts = {}) => {
  if (!Array.isArray(data)) return [];

  return data.reduce((items, item) => {
    if (HIDDEN_CHILD_KEYS[parentKey]?.has(item.key)) return items;

    // An admin gets the whole tree. The API hands them every permission
    // now, so the filter below would pass them anyway — but the sidebar
    // is also drawn from whatever was cached in localStorage on the last
    // sign-in, and an admin should never be looking at a menu narrower
    // than their access because that cache is a version behind.
    const allowed = unrestricted
      || permissions.some((each) => each.key === item.key + "_index");
    if (!allowed) return items;

    const hadChildren = item.children && item.children.length > 0;
    const children = hadChildren
      ? buildMenuItems(item.children, permissions, unrestricted, item.key, counts)
      : null;

    const hasChildren = children && children.length > 0;

    // Every child hidden or filtered out leaves nothing to open — a bare
    // "Case Management" caption over an empty list is worse than no entry.
    if (hadChildren && !hasChildren) return items;

    const count = counts[COUNTED_ROUTES[item.route]];

    items.push({
      key: item.route,
      label: count > 0
        ? <span className="nav-label-with-count">
          <span className="nav-label-text">{ item.name }</span>
          <span className={ `nav-count${ item.route === 'cases/on-hold' ? ' is-alert' : '' }` }
            aria-label={ `${count} waiting` }>{ count > 99 ? '99+' : count }</span>
        </span>
        : item.name,
      icon: <Icon icon={ item.icon } className="nav-icon" />,
      ...(hasChildren ? { children } : {}),
    });
    return items;
  }, []);
};

/** Collect every leaf key so the active route can be matched against them. */
const collectKeys = (items, acc = []) => {
  items.forEach((item) => {
    if (item.children) {
      acc.push(item.key);
      collectKeys(item.children, acc);
    } else {
      acc.push(item.key);
    }
  });
  return acc;
};

/**
 * The sidebar highlight used to be computed once on mount from
 * window.location, so it went stale on back/forward and on deep links.
 * Match the live pathname against the real key list, longest first, so
 * `/cases/details/12` still lights up `cases/total`'s parent group.
 */
const resolveSelection = (pathname, items) => {
  const path = pathname.replace(/^\//, "");
  const keys = collectKeys(items);

  const exact = keys.find((key) => key === path);
  if (exact !== undefined) return exact;

  const prefixed = keys
    .filter((key) => key !== "" && path.startsWith(key + "/"))
    .sort((a, b) => b.length - a.length)[0];

  return prefixed !== undefined ? prefixed : (path === "" ? "" : null);
};

/**
 * Shape of the placeholder rail: the real menu is groups of items, so the
 * skeleton is too — a short group caption, then rows under it. Widths are
 * fixed rather than random so the rail does not reshuffle between renders.
 */
const NAV_SKELETON_GROUPS = [
  { rows: [128, 96, 112] },
  { rows: [104, 136, 88, 120] },
  { rows: [92, 116] },
];

/**
 * Stand-in for the menu while the nav tree is still being fetched.
 *
 * On a fresh login there is no cached `navigation`, so the rail used to
 * sit empty and white between the brand and the footer for the length of
 * the request — it read as a broken sidebar rather than a loading one.
 * Rows match the real item geometry (44px tall, 20px icon, same radius
 * and gutters) so nothing shifts when the menu arrives.
 *
 * Built from plain elements rather than antd's Skeleton: Skeleton.Input
 * wraps itself in a shrink-to-fit span, so a percentage width on the bar
 * resolved to nothing and only the icon block showed up.
 */
export const NavMenuSkeleton = () => (
  <div className="nav-skeleton" aria-hidden="true">
    { NAV_SKELETON_GROUPS.map((group, g) => (
      <div className="nav-skeleton-group" key={ g }>
        <span className="nav-skel nav-skel-caption" />
        { group.rows.map((width, i) => (
          <div className="nav-skeleton-row" key={ i }>
            <span className="nav-skel nav-skel-icon" />
            <span className="nav-skel nav-skel-text" style={ { width } } />
          </div>
        )) }
      </div>
    )) }
  </div>
);

/**
 * Left navigation.
 *
 * Renders as a fixed rail on desktop. Under 992px Main.jsx hides this
 * and shows MobileNavDrawer instead, which reuses the same NavMenu.
 */
export const NavMenu = ({ onNavigate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissionsdata = useSelector((state) => state.auth.permissions);
  const [navigation, setNavigation] = useState([]);
  const [permissions, setPermissions] = useState(
    Array.isArray(permissionsdata) ? permissionsdata : []
  );
  const [openKeys, setOpenKeys] = useState([]);
  // only true while the first fetch is in flight — a cache hit never spins
  const [loading, setLoading] = useState(!localStorage.getItem('navigation'));

  /*
   * Both halves of the menu are cached in localStorage, and both used to
   * be written once and never revisited: the nav tree was fetched only
   * when its cache was empty, and permissions only at login. So a page
   * added to the sidebar, or a permission granted to a role, stayed
   * invisible to everyone already signed in until they happened to log
   * out — which is how a new On Hold page shipped to nobody.
   *
   * Read the cache to paint immediately, then revalidate in the
   * background and keep whatever comes back. No skeleton on the
   * revalidate: the rail is already showing the last known menu.
   */
  useEffect(() => {
    const cachedNav = localStorage.getItem('navigation');
    const cachedPermissions = localStorage.getItem('permissions');

    if (cachedNav) setNavigation(JSON.parse(cachedNav));
    if (cachedPermissions) setPermissions(JSON.parse(cachedPermissions));

    makeRequestStateless(getNavigationReq, null, onSuccess, onFailure);
    makeRequestStateless(getMyPermissionsReq, null, onPermissionsSuccess, Function);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSuccess = (data) => {
    localStorage.setItem('navigation', JSON.stringify(data));
    setNavigation(data);
    setLoading(false);
  };

  const onPermissionsSuccess = (data) => {
    if (!Array.isArray(data)) return;
    localStorage.setItem('permissions', JSON.stringify(data));
    setPermissions(data);
  };

  // Drop the skeleton on failure too, otherwise a dead nav request leaves
  // the rail pulsing forever, which is worse than an honest empty one.
  const onFailure = () => setLoading(false);

  const { counts } = useCaseCounts();

  const items = useMemo(
    () => buildMenuItems(navigation, permissions, isAdmin(), null, counts),
    [navigation, permissions, counts]
  );

  const selectedKey = useMemo(
    () => resolveSelection(location.pathname, items),
    [location.pathname, items]
  );

  // keep the containing group open as the route changes
  useEffect(() => {
    if (selectedKey === null || selectedKey === undefined) return;
    const parent = items.find(
      (item) => item.children && item.children.some((child) => child.key === selectedKey)
    );
    if (parent) {
      setOpenKeys((keys) => (keys.includes(parent.key) ? keys : [...keys, parent.key]));
    }
  }, [selectedKey, items]);

  const handleClick = ({ key }) => {
    navigate('/' + key);
    if (onNavigate) onNavigate();
  };

  if (loading) {
    return <NavMenuSkeleton />;
  }

  return (
    <Menu
      mode="inline"
      theme="light"
      className="nav-menu"
      inlineIndent={ 14 }
      openKeys={ openKeys }
      onOpenChange={ setOpenKeys }
      selectedKeys={ selectedKey === null ? [] : [selectedKey] }
      onClick={ handleClick }
      items={ items }
    />
  );
};

/** Brand lockup — shared by the rail and the mobile drawer. */
export const NavBrand = ({ onClick }) => {
  const settings = useSelector((state) => state.auth.settings);
  const customLogo = settings && settings.logo;

  return (
    <div className="nav-brand" onClick={ onClick } role="button" tabIndex={ 0 }
      onKeyDown={ (e) => e.key === 'Enter' && onClick && onClick() }>
      { /* the stock lockup carries its own wordmark, so it stands alone;
           an uploaded logo is only a mark and keeps the name beside it */ }
      { customLogo
        ? <>
            <span className="nav-brand-mark">
              <img src={ process.env.REACT_APP_BASE_URL + settings.logo } alt="" />
            </span>
            <span className="nav-brand-text">
              <span className="nav-brand-name">{ settings.title || "SmileCorrect" }</span>
              <span className="nav-brand-sub">Case management</span>
            </span>
          </>
        : <img className="nav-brand-logo" src={ Logo } alt="The Smile Correct" /> }
    </div>
  );
};

/**
 * The rail is a fixed 268px on desktop and a drawer below 992px. There is
 * no icon-only middle state: shrinking it left every item as a blank pill
 * and cost more than the 190px it bought back.
 */
const NavSide = () => {
  const navigate = useNavigate();

  return (
    <Sider width={ SIDER_WIDTH } trigger={ null } theme="light" className="app-sider">
      <NavBrand onClick={ () => navigate('/') } />

      <div className="nav-scroll">
        <NavMenu />
      </div>

      <div className="nav-foot">
        <Icon icon="tabler:lifebuoy" width={ 18 } />
        <span>Need help? Contact support</span>
      </div>
    </Sider>
  );
};

export default NavSide;
